import { modalController, SearchbarCustomEvent } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Host, Prop, State } from '@stencil/core';
import { Constants, FileDoc, FileType, PersonDoc } from 'common';
import { ChangeEvent } from 'src/global/ConcourseDb';
import { User } from 'src/global/models';
import { FileService } from 'src/global/service/FileService';
import { PeopleService } from 'src/global/service/PeopleService';
import { ConcoursePhotos } from '../concourse-photos/concourse-photos';
import { DocumentViewerModal } from './document-viewer-modal';
import { SharedUtils } from 'src/global';

@Component({
  tag: 'document-selector-modal',
  styleUrls: ['../../global/styles/concourse-documents.css', '../../global/modals.css'],
  shadow: true,
})
export class DocumentSelectorModal {
  static async present(projectId: string, selectableTypes: FileType[] | null): Promise<FileDoc[]> {
    const modal = await modalController.create({
      component: 'document-selector-modal',
      componentProps: { projectId, selectableTypes },
      cssClass: 'modal-height-100',
      showBackdrop: true,
    });

    return new Promise((resolve, reject) => {
      modal.addEventListener('selectAll', async (e: CustomEvent) => {
        resolve(e.detail);
        modal.dismiss();
      });

      modal.addEventListener('dismiss', () => {
        reject(new Error('Modal dismissed'));
        modal.dismiss();
      });

      modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() selectableTypes: FileType[];

  @State() folderId: string;
  @State() files: FileDoc[] = [];
  @State() selected: FileDoc[] = [];
  @State() viewThumbs: boolean;

  @State() searchTerm: string;

  @Event() dismiss: EventEmitter;
  @Event() selectAll: EventEmitter<FileDoc[]>;
  @State() pendingUploads: File[] = [];

  private browseButton: HTMLInputElement;
  private people: PersonDoc[];

  get user(): User {
    return globalThis.user;
  }

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  async componentWillLoad() {
    const db = FileService.getFilesDb(this.projectId);
    db.on('complete', (docs: FileDoc[]) => {
      this.parseDocs(docs);
    });
    db.on('change', async (e: ChangeEvent) => {
      this.parseDocs(await db.getDocs());
    });
    await this.parseDocs(await db.getDocs());

    this.people = await PeopleService.getPersonDocs(this.projectId);

    if (this.selectableTypes && this.selectableTypes.length == 1) {
      this.folderId = this.getRootFolderId(this.selectableTypes[0]);
    }
  }

  async parseDocs(docs: FileDoc[]) {
    this.files = docs.filter(f => !ConcoursePhotos.FILE_TYPES.includes(f.type));
  }

  getRootFolderId(type: FileType): string | null {
    if (type == FileType.DRAWING) return Constants.ROOT_DRAWINGS_FOLDER_ID;
    if (type == FileType.SCRAPBOOK) return Constants.ROOT_SCRAPBOOKS_FOLDER_ID;
    if (type == FileType.SPECIFICATION) return Constants.ROOT_SPECIFICATIONS_FOLDER_ID;
    return null;
  }

  onSearchInput(e: CustomEvent<string>) {
    this.searchTerm = e.detail.trim().toLowerCase();
  }

  onToggleSelected(file: FileDoc) {
    if (this.selected.indexOf(file) == -1) {
      this.selected = [...this.selected, file];
    } else {
      this.selected = this.selected.filter(p => p._id != file._id);
    }
  }

  onViewFile(file: FileDoc) {
    if (!file) {
      this.folderId = null;
    } else if (file.type == FileType.FOLDER || file.type == FileType.SET) {
      this.folderId = file._id;
    } else {
      DocumentViewerModal.present(this.projectId, file, true);
    }
  }

  async addPendingFiles(files: File[]) {
    const transfer = new DataTransfer();
    files.forEach(f => transfer.items.add(f));
    this.pendingUploads = await SharedUtils.filterAndValidateUploads(transfer.files, this.pendingUploads);
  }

  render() {
    return (
      <Host class="concourse-modal">
        <modal-header modalTitle="Document Selector" enableSearch={true} onSearchInput={e => this.onSearchInput(e)} onDismiss={() => this.dismiss.emit()}>
          <ion-button id="toggle-view" onClick={() => (this.viewThumbs = !this.viewThumbs)}>
            {this.viewThumbs ? <ion-icon slot="icon-only" src="/assets/icon/table-icon.svg" /> : <ion-icon slot="icon-only" name="grid-outline" />}
          </ion-button>
        </modal-header>
        <div class="cc-scrollbars modal-content">
          <concourse-documents-list
            projectId={this.projectId}
            selectableTypes={this.selectableTypes}
            folderId={this.folderId}
            searchTerm={this.searchTerm}
            viewThumbs={this.viewThumbs}
            files={this.files}
            people={this.people}
            selected={this.selected}
            onToggleSelected={e => this.onToggleSelected(e.detail)}
            onViewFile={e => this.onViewFile(e.detail)}
          />
        </div>
        <ion-footer>
          <ion-toolbar>
            {this.selected.length > 0 ? (
              <ion-button slot="start" class="link-button" fill="clear" onClick={() => (this.selected = [])}>
                <ion-icon slot="start" name="close-outline"></ion-icon>
                <ion-label>clear Selection</ion-label>
              </ion-button>
            ) : (
              <ion-button slot="start" class="link-button" fill="clear" onClick={() => this.browseButton.click()}>
                <ion-icon slot="start" name="cloud-upload-outline"></ion-icon>
                <ion-label>Upload</ion-label>
              </ion-button>
            )}
            <ion-button slot="end" disabled={this.selected.length == 0} onClick={() => this.selectAll.emit(this.selected)}>
              <ion-label>
                select {this.selected.length} document{this.selected.length > 0 && 's'}
              </ion-label>
            </ion-button>
          </ion-toolbar>
        </ion-footer>
        <input
          id="browse-button"
          type="file"
          ref={el => (this.browseButton = el)}
          onChange={e => {
            this.addPendingFiles(Array.from((e.target as HTMLInputElement).files));
            (e.target as HTMLInputElement).value = '';
          }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp"
          hidden
        />
      </Host>
    );
  }
}
